import { useMemo, useState } from "react";

import { useT } from "../../i18n";
import type { Plume } from "../../data/types";
import { useFormat } from "../../lib/format";
import { useElementSize } from "../../hooks/useElementSize";

const HEIGHT = 120;
const PAD = { top: 10, right: 8, bottom: 18, left: 34 };

interface Point {
  x: number;
  y: number;
  date: string;
  rate: number;
}

/**
 * Rate-by-date for one episode (brief §10.5). Quantified detections are drawn
 * as a line of points; unquantified ones (null rate) appear as hollow ticks on
 * the baseline so they aren't silently dropped. Hover reveals value + date.
 */
export function EmissionChart({ plumes }: { plumes: Plume[] }) {
  const t = useT();
  const fmt = useFormat();
  const [ref, { width }] = useElementSize<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const quantified = plumes.filter((p) => p.emission_rate_kg_hr !== null);

  const { points, nullTicks, maxRate } = useMemo(() => {
    if (width === 0 || plumes.length === 0) {
      return { points: [] as Point[], nullTicks: [] as number[], maxRate: 0 };
    }
    const times = plumes.map((p) => Date.parse(p.sensing_time));
    const minT = Math.min(...times);
    const maxT = Math.max(...times);
    const spanT = maxT - minT || 1;
    const maxR = Math.max(1, ...quantified.map((p) => p.emission_rate_kg_hr as number)) * 1.1;

    const innerW = width - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const xOf = (ms: number) => PAD.left + ((ms - minT) / spanT) * innerW;
    const yOf = (rate: number) => PAD.top + innerH - (rate / maxR) * innerH;

    const pts: Point[] = quantified.map((p) => ({
      x: xOf(Date.parse(p.sensing_time)),
      y: yOf(p.emission_rate_kg_hr as number),
      date: p.sensing_time.slice(0, 10),
      rate: p.emission_rate_kg_hr as number,
    }));
    const ticks = plumes
      .filter((p) => p.emission_rate_kg_hr === null)
      .map((p) => xOf(Date.parse(p.sensing_time)));
    return { points: pts, nullTicks: ticks, maxRate: maxR };
  }, [width, plumes, quantified]);

  const baselineY = HEIGHT - PAD.bottom;

  return (
    <div ref={ref} className="relative w-full">
      {quantified.length === 0 ? (
        <p className="py-4 text-center text-xs text-text-tertiary">{t.detail.chartEmpty}</p>
      ) : (
        <svg width={width} height={HEIGHT} className="block">
          {/* y-axis max label */}
          <text x={0} y={PAD.top + 4} className="fill-text-tertiary" style={{ fontSize: 9 }}>
            {fmt.int(maxRate)}
          </text>
          <line
            x1={PAD.left}
            y1={baselineY}
            x2={width - PAD.right}
            y2={baselineY}
            className="stroke-border-subtle"
          />
          {points.length > 1 && (
            <polyline
              points={points.map((p) => `${p.x},${p.y}`).join(" ")}
              fill="none"
              className="stroke-accent"
              strokeWidth={1.5}
            />
          )}
          {nullTicks.map((x, i) => (
            <line
              key={`n-${i}`}
              x1={x}
              y1={baselineY - 4}
              x2={x}
              y2={baselineY}
              className="stroke-text-tertiary"
            />
          ))}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={hover === i ? 4 : 2.5}
              className="fill-accent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      )}

      {hover !== null && points[hover] && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded border border-border-subtle bg-surface-overlay px-2 py-1 text-xs shadow-md"
          style={{ left: points[hover].x, top: points[hover].y - 38 }}
        >
          <div className="data-value font-medium text-text-primary">
            {fmt.int(points[hover].rate)} {t.detail.kgh}
          </div>
          <div className="data-value text-text-tertiary">{fmt.date(points[hover].date)}</div>
        </div>
      )}
    </div>
  );
}
